import React, { useEffect, useRef, useState } from "react";
import { UploadCloud, Plus, Loader2, X, Copy, SlidersHorizontal, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { toast } from "sonner";
import AdjustDialog from "./AdjustDialog";
import { AdjustedThumb } from "./AdjustedThumb";

export default function PhotosStep({ draft, setDraft }) {
  const { t } = useT();
  const inputRef = useRef(null);
  const singleRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [adjustSlot, setAdjustSlot] = useState(null);
  const targetSlotRef = useRef(null);
  const kit = draft.kit_qty || 0;

  // ensure magnets array matches kit size
  useEffect(() => {
    setDraft((d) => {
      const arr = Array.isArray(d.magnets) ? [...d.magnets] : [];
      while (arr.length < kit) arr.push(null);
      arr.length = kit;
      return { ...d, magnets: arr };
    });
  }, [kit, setDraft]);

  const magnets = draft.magnets || [];
  const filled = magnets.filter(Boolean).length;

  const uploadFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const r = await api.post("/photos/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    return {
      photo_id: r.data.photo_id, preview_w: r.data.preview_w, preview_h: r.data.preview_h,
      original_w: r.data.original_w, original_h: r.data.original_h, filename: r.data.original_filename,
      adjusted: false, crop: null, rotation: 0, zoom: 1, cropPos: { x: 0, y: 0 },
    };
  };

  const addFiles = async (files, startSlot = null) => {
    const list = Array.from(files);
    setUploading((u) => u + list.length);
    for (const file of list) {
      try {
        const magnet = await uploadFile(file);
        setDraft((d) => {
          const arr = [...d.magnets];
          let idx = startSlot != null && arr[startSlot] == null ? startSlot : arr.findIndex((x) => x == null);
          if (idx === -1) { toast.info(`All ${kit} slots are full.`); return d; }
          arr[idx] = magnet;
          startSlot = null;
          return { ...d, magnets: arr };
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed.");
      } finally {
        setUploading((u) => Math.max(0, u - 1));
      }
    }
  };

  const removeSlot = (i) => {
    const m = magnets[i];
    if (m?.photo_id && !magnets.some((x, j) => j !== i && x?.photo_id === m.photo_id)) {
      api.delete(`/photos/${m.photo_id}`).catch(() => {});
    }
    setDraft((d) => { const arr = [...d.magnets]; arr[i] = null; return { ...d, magnets: arr }; });
  };

  const duplicate = (i) => {
    setDraft((d) => {
      const arr = [...d.magnets];
      const empty = arr.findIndex((x) => x == null);
      if (empty === -1) { toast.info(`All ${kit} slots are full.`); return d; }
      arr[empty] = { ...arr[i] };
      return { ...d, magnets: arr };
    });
  };

  const openAdjust = (i) => setAdjustSlot(i);
  const saveAdjust = (local) => {
    setDraft((d) => {
      const arr = [...d.magnets];
      arr[adjustSlot] = { ...arr[adjustSlot], cropPos: local.cropPos, zoom: local.zoom, rotation: local.rotation, crop: local.crop, adjusted: !!local.crop };
      return { ...d, magnets: arr };
    });
    setAdjustSlot(null);
    toast.success(t("adjusted"));
  };

  const replaceSlot = async (i, file) => {
    try {
      const m = await uploadFile(file);
      const old = magnets[i]?.photo_id;
      if (old && !magnets.some((x, j) => j !== i && x?.photo_id === old)) api.delete(`/photos/${old}`).catch(() => {});
      setDraft((d) => { const arr = [...d.magnets]; arr[i] = m; return { ...d, magnets: arr }; });
    } catch (e) { toast.error("Replace failed."); }
  };

  return (
    <div>
      <p className="text-sm font-medium">{t("photos_hint")}</p>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">{t("photos_sub")}</p>

      <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" data-testid="photo-upload-input"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      <input ref={singleRef} type="file" accept="image/*,.heic,.heif" className="hidden"
        onChange={(e) => { if (e.target.files[0]) { const mode = targetSlotRef.current; if (mode?.type === "replace") replaceSlot(mode.i, e.target.files[0]); else addFiles(e.target.files, mode?.i); } e.target.value = ""; }} />

      <Button onClick={() => inputRef.current?.click()} data-testid="add-photos-button" className="mt-4 h-12 w-full rounded-xl">
        <UploadCloud className="mr-2 h-5 w-5" /> {t("add_photos")}
      </Button>

      <div className="mt-2 flex items-center justify-between">
        <Badge variant="secondary" data-testid="fill-counter" className="rounded-full px-3 py-1 text-xs">{t("filled_of", { done: filled, total: kit })}</Badge>
        {uploading > 0 && <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {uploading}</span>}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
        {magnets.map((m, i) => (
          <div key={i} data-testid={m ? "photo-slot-filled" : "photo-slot-empty"}
            className={`relative aspect-square overflow-hidden bg-white ${m ? "shadow-[0_5px_14px_rgba(74,42,39,0.16)]" : "border border-dashed"}`}
            style={{ borderRadius: "20%" }}>
            {m ? (
              <>
                <AdjustedThumb magnet={m} className="h-full w-full object-cover" />
                <span className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.30), rgba(255,255,255,0) 42%)" }} />
                <span className="absolute left-1 top-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold text-white"
                  style={{ background: m.adjusted ? "hsl(var(--success))" : "hsl(var(--brand-rose))" }}>
                  {m.adjusted ? t("adjusted") : t("framing_us")}
                </span>
                <button onClick={() => removeSlot(i)} className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/50 text-white"><X className="h-3 w-3" /></button>
                <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-gradient-to-t from-black/55 to-transparent p-1">
                  <button onClick={() => openAdjust(i)} data-testid={`adjust-button-${i}`} className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-medium"><SlidersHorizontal className="h-3 w-3" /> {t("adjust")}</button>
                  <button onClick={() => duplicate(i)} title={t("duplicate")} className="grid h-6 w-6 place-items-center rounded-full bg-white/95"><Copy className="h-3 w-3" /></button>
                </div>
              </>
            ) : (
              <button onClick={() => { targetSlotRef.current = { i, type: "add" }; singleRef.current?.click(); }} data-testid={`empty-slot-${i}`}
                className="flex h-full w-full flex-col items-center justify-center gap-1 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">
                <Plus className="h-5 w-5" /><span className="text-[10px]">{t("slot_add")}</span>
              </button>
            )}
          </div>
        ))}
      </div>

      <p className="mt-4 rounded-lg bg-[hsl(var(--muted))] p-3 text-[11px] text-[hsl(var(--muted-foreground))]">{t("quality_note")}</p>

      <AdjustDialog open={adjustSlot != null} magnet={adjustSlot != null ? magnets[adjustSlot] : null} onClose={() => setAdjustSlot(null)} onSave={saveAdjust} />
    </div>
  );
}
